import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as queryString from 'query-string';
import React from 'react';
import { normalize, schema } from 'normalizr';
import { FilterValues, getCurrentFilterValue, getValueData, parseFilters } from './filterUtils';
import { applyAllRules, applyRules } from './filterRulesHandler';
import { FilterBaseType } from '../../type/FilterBaseType';
import { FilterResolveType } from '../../type/FilterResolveType';
import { getFilterConfiguration } from '../../api/configurationAPI';
import { FilterDTO } from '../../dto/filter/FilterDTO';
import { instanceOfOptionValueArray } from 'festo-react-ui-kit';

interface FilterConfigurationState {
  isLoading: boolean;
  error: string | null;
  filters: Record<string, FilterBaseType>;
  subFilterKeys: string[];
  filterKeys: string[];
}

export interface FilterParameter {
  id: number;
  value: React.ReactText[];
  resolve: FilterResolveType;
}

const initialState: FilterConfigurationState = {
  isLoading: false,
  error: null,
  filters: {},
  subFilterKeys: [],
  filterKeys: [],
};

interface Acceleration {
  horizontalAcceleration?: number;
  verticalAcceleration?: number;
}
interface VacuumGenerator {
  maxEvacuationTime?: number;
  pressure?: string;
  onOffValve?: boolean;
  airSaving?: boolean;
  ejectorPulse?: boolean;
  display?: boolean;
  sensor?: boolean;
  porous?: boolean;
}

interface WorkPiece {
  length?: number;
  mass?: number;
  radius?: number;
  width?: number;
  fda?: boolean;
  highTemperature?: boolean;
  minimalMarking?: boolean;
  antistatic?: boolean;
}
export interface ResultsRequestBody {
  acceleration?: Acceleration;
  vacuumGenerator?: VacuumGenerator;
  surfaceTexture?: string;
  surfaceShape?: string;
  workPiece?: WorkPiece;
}

export function generateResultsRequestBody(filters: FilterBaseType[]): ResultsRequestBody {
  const output: ResultsRequestBody = {};
  const accelerationKeys = ['horizontalAcceleration', 'verticalAcceleration'];
  const vacuumGeneratorKeys = [
    'maxEvacuationTime',
    'pressure',
    'onOffValve',
    'airSaving',
    'ejectorPulse',
    'display',
    'sensor',
    'porous',
  ];
  const topLevelKeys = ['surfaceTexture', 'surfaceShape'];
  const workPieceKeys = ['length', 'mass', 'radius', 'width', 'fda', 'highTemperature', 'minimalMarking', 'antistatic'];
  const stringFilterValuesKeys = ['surfaceTexture', 'surfaceShape', 'pressure'];

  const filterMap = new Map<string, any>();
  filters.forEach((filter) => {
    const filterValue = getValueData(filter);
    if (filterValue) {
      if (filter.key === 'additionalFunctions' || filter.key === 'environment' || filter.key === 'porous') {
        filterValue.forEach((value: string) => {
          filterMap.set(value, true);
        });
      } else {
        const typeSafeFilterValue = stringFilterValuesKeys.includes(filter.key)
          ? filterValue[0]
          : Number(filterValue[0]);
        filterMap.set(filter.key, typeSafeFilterValue);
      }
    }
  });

  const accelerationData: Acceleration = {};
  accelerationKeys.forEach((key) => {
    if (filterMap.has(key)) {
      accelerationData[key as keyof Acceleration] = filterMap.get(key);
    }
  });
  if (Object.keys(accelerationData).length > 0) {
    output.acceleration = accelerationData;
  }

  const vacuumGeneratorData: VacuumGenerator = {};
  vacuumGeneratorKeys.forEach((key) => {
    if (filterMap.has(key)) {
      vacuumGeneratorData[key as keyof VacuumGenerator] = filterMap.get(key);
    }
  });
  if (Object.keys(vacuumGeneratorData).length > 0) {
    output.vacuumGenerator = vacuumGeneratorData;
  }

  topLevelKeys.forEach((key) => {
    if (filterMap.has(key)) {
      output[key as 'surfaceTexture' | 'surfaceShape'] = filterMap.get(key);
    }
  });

  const workPieceData: WorkPiece = {};

  workPieceKeys.forEach((key) => {
    if (filterMap.has(key)) {
      workPieceData[key as keyof WorkPiece] = filterMap.get(key);
    }
  });

  if (Object.keys(workPieceData).length > 0) {
    output.workPiece = workPieceData;
  }

  return output;
}

export function getBackendQuery(filters: Record<string, FilterBaseType>): { body: ResultsRequestBody; query: string } {
  const outputQuery = parseFilters(Object.values(filters), 'backend');
  const query = queryString.stringify(outputQuery);
  const transformedOutputQuery = generateResultsRequestBody(Object.values(filters));
  return { body: transformedOutputQuery, query };
}

export function getFrontendQuery(filters: Record<string, FilterBaseType>): { queryObj: FilterValues; query: string } {
  const existingParams = new URLSearchParams(window.location.search);
  const existingObj = Object.fromEntries(existingParams);
  const outputQuery = parseFilters(Object.values(filters), 'frontend');
  const mergedQuery = { ...existingObj, ...outputQuery };
  const query = queryString.stringify(mergedQuery, { arrayFormat: 'comma' });
  return { queryObj: outputQuery, query };
}

function applyValueFromUrl(state: FilterConfigurationState) {
  const inputQuery = queryString.parse(window.location.search, { arrayFormat: 'comma' });

  state.filterKeys.concat(state.subFilterKeys).forEach((key) => {
    const queryParameter = inputQuery[state.filters[key].key];
    if (queryParameter !== null && queryParameter !== undefined) {
      const filter = state.filters[key];
      state.filters[key] = {
        ...filter,
        value: getCurrentFilterValue(filter, Array.isArray(queryParameter) ? queryParameter : [queryParameter]),
      };
    }
  });
}

function startLoading(state: FilterConfigurationState) {
  state.isLoading = true;
}
export const fetchFilters = createAsyncThunk('fetchFilters', async (query: string) => {
  const configuration = await getFilterConfiguration(query);
  const subFilter = new schema.Entity('subFilters', {});

  const filter = new schema.Entity('filters', {
    filters: [subFilter],
  });
  const {
    entities: { filters, subFilters },
  } = normalize<
    FilterDTO[],
    { filters: Record<string, FilterBaseType>; subFilters: Record<string, FilterBaseType> },
    string[]
  >(configuration, [filter]);
  const result = {
    filters: { ...filters, ...subFilters },
    filterKeys: Object.keys(filters),
    subFilterKeys: subFilters !== undefined ? Object.keys(subFilters) : [],
  };

  return result;
});

export const filterSlice = createSlice({
  name: 'filter',
  initialState,
  reducers: {
    updateFilterConfigValue: (state, action: PayloadAction<FilterParameter>) => {
      const filter = state.filters[action.payload.id];
      const values = filter.value;

      if (values && instanceOfOptionValueArray(values)) {
        values.forEach((value) => {
          value.disabled = false;
        });
      }

      state.filters[action.payload.id] = { ...filter, value: getCurrentFilterValue(filter, action.payload.value) };
      applyRules(action.payload.value, state.filters, filter);
      const { query } = getFrontendQuery(state.filters);
      window.history.replaceState(null, '', `${window.location.pathname}?${query}`);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchFilters.pending, startLoading);
    builder.addCase(fetchFilters.fulfilled, (state, { payload }) => {
      state.isLoading = false;
      state.error = null;
      state.filters = payload.filters;
      state.filterKeys = payload.filterKeys;
      state.subFilterKeys = payload.subFilterKeys;
      applyValueFromUrl(state);
      const allKeys = state.filterKeys.concat(state.subFilterKeys);
      applyAllRules(allKeys, state.filters);
      const { query } = getFrontendQuery(state.filters);
      window.history.replaceState(null, '', `${window.location.pathname}?${query}`);
    });
    builder.addCase(fetchFilters.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || 'Fetching filters failed';
    });
  },
});

export const { updateFilterConfigValue } = filterSlice.actions;

export default filterSlice.reducer;
